import { Box, ChevronBackButton, ChevronForwardButton, Text } from '@opendoor/bricks-next';
import type { ISiteFooter, ISiteHeader } from 'declarations/contentful';
import { useRouter } from 'next/router';

import MarketplaceMapLayout from '../../properties/shared/MarketplaceMapLayout';
import { ListingCard } from '../property/SimilarListings';
import type { ListingCardData } from '../property/SimilarListings';
import { CityLinkList } from './CityLinkList';
import type { CityLink } from './CityLinkList';
import { DirectoryHeader } from './DirectoryHeader';
import { DirectorySeoHead } from './DirectorySeoHead';
import { StateLinkList } from './StateLinkList';
import type { StateLink } from './StateLinkList';

const PAGE_SIZE = 30;

interface DirectoryTemplateProps {
  aiBlurb?: string | null;
  canonicalUrl: string;
  citiesWithInventory: CityLink[];
  currentPage: number;
  footer?: ISiteFooter | null;
  header?: ISiteHeader | null;
  listings: ListingCardData[];
  locationName: string;
  locationSlug: string;
  marketSlug?: string;
  nearMeCity?: string | null;
  nearMeCityCount?: number | null;
  nextPage?: number | null;
  pageType: 'city' | 'state';
  seoDescription: string;
  seoTitle: string;
  stateName?: string | null;
  statesWithInventory: StateLink[];
  totalCount: number;
  displayingCity?: string | null;
  availableCities?: Array<{ name: string; slug: string }> | null;
}

export const DirectoryTemplate: React.FC<DirectoryTemplateProps> = (props) => {
  const {
    aiBlurb,
    availableCities,
    canonicalUrl,
    citiesWithInventory,
    currentPage,
    displayingCity,
    footer,
    header,
    listings,
    locationName,
    locationSlug,
    marketSlug,
    nearMeCity,
    nearMeCityCount,
    nextPage,
    pageType,
    seoDescription,
    seoTitle,
    stateName,
    statesWithInventory,
    totalCount,
  } = props;

  const router = useRouter();

  // Use API data directly, only calculate totalPages for display
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const showPagination = totalCount > PAGE_SIZE;
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = nextPage != null;

  // Build pagination URL preserving existing query params (like city selection)
  const buildPageUrl = (page: number) => {
    const params = new URLSearchParams();
    // Copy query params except dynamic route segments
    Object.entries(router.query).forEach(([key, value]) => {
      if (key !== 'state' && key !== 'market' && typeof value === 'string') {
        params.set(key, value);
      }
    });
    params.set('page', String(page));
    const basePath = router.asPath.split('?')[0];
    return `${basePath}?${params.toString()}`;
  };
  return (
    <>
      <DirectorySeoHead
        canonicalUrl={canonicalUrl}
        listings={listings}
        locationName={locationName}
        pageType={pageType}
        seoDescription={seoDescription}
        seoTitle={seoTitle}
        stateName={stateName}
        totalCount={totalCount}
      />
      <MarketplaceMapLayout header={header} footer={footer ?? undefined}>
        <Box py="$8x" px="$4x" height="100%">
          <Box maxWidth={1200} mx="auto" height="100%" overflow="scroll">
            <DirectoryHeader
              pageType={pageType}
              locationName={locationName}
              locationSlug={locationSlug}
              totalCount={totalCount}
              stateName={stateName}
              displayingCity={displayingCity}
              availableCities={availableCities}
              nearMeCity={nearMeCity}
              nearMeCityCount={nearMeCityCount}
              marketSlug={marketSlug || availableCities?.[0].slug}
            />

            {aiBlurb && (
              <Text tag="p" typography="$bodyLarge" mb="$4x">
                {aiBlurb}
              </Text>
            )}

            <Box flexDirection="row" flexWrap="wrap" mb="$8x" style={{ gap: '1.5rem' }}>
              {listings.map((listing, index) => (
                <Box
                  key={`${listing.slug}-${index}`}
                  width="100%"
                  $largerThanSM={{
                    width: 'calc((100% - 1.5rem) / 2)',
                  }}
                  $largerThanMD={{
                    width: 'calc((100% - 1.5rem) / 2)',
                  }}
                >
                  <ListingCard listing={listing} />
                </Box>
              ))}
            </Box>

            {showPagination && (
              <Box
                flexDirection="row"
                justifyContent="center"
                alignItems="center"
                gap="$4x"
                mb="$8x"
              >
                <ChevronBackButton
                  analyticsName="directory-previous-page"
                  size="medium"
                  href={buildPageUrl(currentPage - 1)}
                  disabled={!hasPreviousPage}
                  iconLabel="Go to previous page"
                />
                <Text tag="span" typography="$bodyMedium">
                  Page {currentPage} of {totalPages}
                </Text>
                <ChevronForwardButton
                  analyticsName="directory-next-page"
                  size="medium"
                  href={buildPageUrl(currentPage + 1)}
                  disabled={!hasNextPage}
                  iconLabel="Go to next page"
                />
              </Box>
            )}

            <Box display="flex" flexDirection="row">
              <CityLinkList cities={citiesWithInventory} />
              <StateLinkList states={statesWithInventory} />
            </Box>
          </Box>
        </Box>
      </MarketplaceMapLayout>
    </>
  );
};
